import type { SxProps, Theme } from "@mui/material/styles";

const ACCENT = "#7B49C1";
const ACCENT_SOFT = "rgba(123, 73, 193, 0.12)";
const ACCENT_BORDER = "rgba(123, 73, 193, 0.18)";

const SEND_GREEN = "linear-gradient(135deg, #5EEB7A 0%, #3DD15C 50%, #2BB84A 100%)";
const SEND_GREEN_HOVER =
  "linear-gradient(135deg, #6FF08A 0%, #48DB6B 50%, #34C456 100%)";
const SEND_GREEN_DISABLED =
  "linear-gradient(135deg, #B7E8C0 0%, #8FD99E 100%)";

export const stickyBarSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  mb: 0.25,
  px: { xs: 0.75, sm: 1 },
  py: { xs: 0.35, sm: 0.45 },
  backgroundColor: "transparent",
};

export const actionsRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "stretch",
  gap: { xs: 0.6, sm: 0.85 },
  width: "100%",
};

export const getModeSendButtonSx =
  (isDisabled = false): SxProps<Theme> => ({
    flex: "1 1 0",
    minWidth: 0,
    // Match footer input slide height (`getInputSlideSx` / `VOICE_INPUT_HEIGHT`).
    minHeight: 40,
    height: 40,
    maxHeight: 40,
    borderRadius: "999px",
    border: "1px solid",
    borderColor: ACCENT_BORDER,
    opacity: isDisabled ? 0.55 : 1,
    color: ACCENT,
    backgroundColor: "#fff",
    textTransform: "none",
    fontWeight: 600,
    fontSize: { xs: "10px", sm: "13px" },
    letterSpacing: { xs: "-0.01em", sm: "-0.005em" },
    lineHeight: 1.2,
    justifyContent: "flex-start",
    alignItems: "center",
    gap: { xs: 0.45, sm: 0.7 },
    px: { xs: 0.45, sm: 0.55 },
    py: 0,
    pr: { xs: 0.4, sm: 0.45 },
    boxShadow: "none",
    whiteSpace: { xs: "normal", sm: "nowrap" },
    textAlign: "left",
    transition:
      "background 0.2s ease, border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease, transform 0.15s ease",
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: "#F6F1FC",
      borderColor: "rgba(123, 73, 193, 0.4)",
      color: "#6A3AAD",
      boxShadow: "none",
      "& .mode-send-pill": {
        background: SEND_GREEN_HOVER,
      },
    },
    "&:active:not(.Mui-disabled)": {
      transform: "scale(0.99)",
      backgroundColor: "#EFE7F8",
    },
    "&.Mui-disabled": {
      color: ACCENT,
      borderColor: ACCENT_BORDER,
      backgroundColor: "#fff",
    },
  });

export const modeButtonIconSx: SxProps<Theme> = {
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: { xs: 24, sm: 28 },
  height: { xs: 24, sm: 28 },
  borderRadius: "50%",
  backgroundColor: ACCENT_SOFT,
  color: ACCENT,
  lineHeight: 0,
  "& svg": {
    width: { xs: 13, sm: 15 },
    height: { xs: 13, sm: 15 },
  },
};

export const modeButtonLabelSx: SxProps<Theme> = {
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const getModeSendPillSx =
  (readyToSend: boolean): SxProps<Theme> => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.3,
    flexShrink: 0,
    ml: "auto",
    height: { xs: 26, sm: 30 },
    minWidth: { xs: 26, sm: 30 },
    px: { xs: 0.75, sm: 1 },
    borderRadius: "999px",
    fontSize: { xs: "9px", sm: "11px" },
    fontWeight: 700,
    letterSpacing: "0.01em",
    lineHeight: 1,
    color: "#fff",
    background: readyToSend ? SEND_GREEN : SEND_GREEN_DISABLED,
    boxShadow: "none",
    transition: "background 0.2s ease",
    "& svg": {
      width: { xs: 13, sm: 15 },
      height: { xs: 13, sm: 15 },
      display: "block",
    },
  });

export const validationHintSx: SxProps<Theme> = {
  px: { xs: 0.5, sm: 1 },
  pt: 0.25,
  color: "#DD503E",
  fontSize: { xs: "9px", sm: "11px" },
  lineHeight: 1.35,
  fontWeight: 500,
};
